// OnboardingStep3.js
import React, { useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { submitAccessibilityAtom } from "@/store/atoms";
import Switch from "@/components/common/Switch";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useI18N } from "common/hooks";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import WideButton from "@/components/common/WideButton";
import { ArrowLeft } from "lucide-react";

interface OnboardingStep3Props {
  title: string;
  subtitle: string;
}

const OnboardingStep3 = ({ title, subtitle }: OnboardingStep3Props) => {
  const i18n = useI18N();
  const isDark = useDarkMode();

  //set index for swipable views
  const { setScreenIndex } = useAuthModals();

  //get and store the data for entire onBoarding steps
  const [submitData, setSubmitData] = useAtom(submitAccessibilityAtom);

  useEffect(() => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountMakeDisabilityPublicPage"
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountMakeDisabilityPublicPage",
      {}
    );
  }, []);

  const onBack = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountStep3BackButtonClicked"
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountStep3BackButtonClicked",
      {}
    );
    setScreenIndex(AuthModalScreen.OnboardingStep2);
  };

  const onContinue = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountStep3Completed"
    );
    AnalyticsService.trackMx("authPopupCreateAccountStep3Completed", {});

    //move to next step
    setScreenIndex(AuthModalScreen.OnboardingStep4);
  };

  const handleToggleSwitch = () => {
    if (submitData?.makeDisabilityVisibleValue) {
      AnalyticsService.trackGAEventHandler(
        "authPopupCreateAccountMakeDisabilityPublicTurnedOff"
      );
      AnalyticsService.trackMx(
        "authPopupCreateAccountMakeDisabilityPublicTurnedOff",
        {}
      );
    } else {
      AnalyticsService.trackGAEventHandler(
        "authPopupCreateAccountMakeDisabilityPublicTurnedOn"
      );
      AnalyticsService.trackMx(
        "authPopupCreateAccountMakeDisabilityPublicTurnedOn",
        {}
      );
    }

    setSubmitData((prev) => ({
      ...prev,
      makeDisabilityVisibleValue: !(
        submitData?.makeDisabilityVisibleValue || false
      ),
    }));
  };

  console.log("submitData from OnboardingStep2", submitData);

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div
      style={{
        height:
          window.innerHeight < 800
            ? `${window.innerHeight - 40}px`
            : `${window.innerHeight - 100}px`,
      }}
      className="flex flex-col rounded-xl bg-white dark:bg-primary-900 overflow-y-auto"
    >
      <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky top-0">
        <h1
          className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
          tabIndex={0}
          ref={firstFocusRef} // Attach the ref here
        >
          {i18n.get("profileSetup")}
        </h1>
      </header>
      <div className="px-8 md:px-16 flex-1 overflow-y-auto">
        <div>
          <h2
            className="pt-6 text-primary-900 dark:text-primary-100 desktop-h1-xsmall-extra-bold"
            tabIndex={0}
          >
            {title}
          </h2>
          <p
            className="paragraph-medium-medium text-primary-900 dark:text-primary-100 mt-4"
            tabIndex={0}
          >
            {subtitle}
          </p>
        </div>
        <div>
          <hr className="w-full border-primary-150 dark:border-primary-200 my-2" />
        </div>
        <div aria-label="Model Content">
          {/* content comes here */}
          <div>
            <div className="mb-4">
              <p
                className="desktop-h2-medium-semi-bold md:desktop-h4-medium-semi-bold text-primary-900 dark:text-primary-100 mb-3"
                tabIndex={0}
              >
                {i18n.get(
                  "doYouWantToMakeYourDisabilityVisibleToTheSociabilityCommunity"
                )}
              </p>
              <p
                className="paragraph-small-semibold !text-primary-500 dark:!text-primary-300 block"
                tabIndex={0}
              >
                {i18n.get("helpOtherUsersBetterUnderstandYourReviews")}
              </p>
            </div>
            <div className="flex justify-center h-[180px] items-center">
              <img
                src="/images/disabilityVisible.svg"
                alt={i18n.get("helpOtherUsersBetterUnderstandYourReviews")}
                className="h-[180px]"
              />
            </div>
            <div className="flex justify-between gap-4 items-center pt-6">
              <div>
                <p
                  className="paragraph-medium-semi-bold md:desktop-h4-medium-semi-bold mb-1 text-primary-900 dark:text-primary-100"
                  tabIndex={0}
                >
                  {i18n.get("makeYourDisabilityPublic")}
                </p>
                <p
                  className="paragraph-small-regular text-primary-900 dark:text-primary-100"
                  tabIndex={0}
                >
                  {i18n.get("helpOtherUsersBetterUnderstandYourReviews")}
                </p>
              </div>
              <div tabIndex={0}>
                <Switch
                  isOn={submitData?.makeDisabilityVisibleValue || false}
                  onChange={() => handleToggleSwitch()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mb-8 items-center justify-center flex flex-col px-8 md:px-16 pt-2">
        <WideButton type="filled" onClick={() => onContinue()}>
          {i18n.get("continue")}
        </WideButton>
      </div>
      <button onClick={onBack} className="absolute top-4 left-4">
        <ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />
      </button>
    </div>
  );
};

export default OnboardingStep3;
